import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../lib/auth";
import { useCardsStore } from "../lib/store";
import { appendCards, detectTsubameSheet, ensureTagsColumn } from "../lib/sheets";
import { addTag, removeTag } from "../lib/tags";
import TagBadge from "./TagBadge";
import type { BusinessCard } from "../lib/types";

type Step = "form" | "running" | "done" | "error";

interface FormState {
  name: string;
  acquiredAt: string;
  company: string;
  department: string;
  title: string;
  postalCode: string;
  address: string;
  phone: string;
  mobile: string;
  fax: string;
  email1: string;
  email2: string;
  url: string;
  memo: string;
  nameEn: string;
  companyEn: string;
  departmentEn: string;
  titleEn: string;
  addressEn: string;
}

const todayIso = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const initialForm = (): FormState => ({
  name: "",
  acquiredAt: todayIso(),
  company: "",
  department: "",
  title: "",
  postalCode: "",
  address: "",
  phone: "",
  mobile: "",
  fax: "",
  email1: "",
  email2: "",
  url: "",
  memo: "",
  nameEn: "",
  companyEn: "",
  departmentEn: "",
  titleEn: "",
  addressEn: "",
});

export default function ManualEntryFlow() {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const sheet = useCardsStore((s) => s.sheet);
  const setSheet = useCardsStore((s) => s.setSheet);
  const setStatus = useCardsStore((s) => s.setStatus);

  const [step, setStep] = useState<Step>("form");
  const [form, setForm] = useState<FormState>(initialForm);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [showEnglish, setShowEnglish] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const set = <K extends keyof FormState>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submitTagInput = () => {
    const t = tagInput.trim();
    if (!t) return;
    setTags((prev) => addTag(prev, t));
    setTagInput("");
  };

  const buildCard = (): BusinessCard => {
    // YYYY-MM-DD のまま保持。`new Date(...).toISOString()` は TZ ずれの原因に
    // なる(Asia/Tokyo で前日 UTC 日になる) ため避け、表示側でローカル整形する。
    const acquiredAt = form.acquiredAt || todayIso();
    return {
      name: form.name.trim(),
      acquiredAt,
      company: form.company.trim() || undefined,
      department: form.department.trim() || undefined,
      title: form.title.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      address: form.address.trim() || undefined,
      phone: form.phone.trim() || undefined,
      mobile: form.mobile.trim() || undefined,
      fax: form.fax.trim() || undefined,
      email1: form.email1.trim() || undefined,
      email2: form.email2.trim() || undefined,
      url: form.url.trim() || undefined,
      memo: form.memo.trim() || undefined,
      nameEn: form.nameEn.trim() || undefined,
      companyEn: form.companyEn.trim() || undefined,
      departmentEn: form.departmentEn.trim() || undefined,
      titleEn: form.titleEn.trim() || undefined,
      addressEn: form.addressEn.trim() || undefined,
      tags: tags.length > 0 ? tags : undefined,
    };
  };

  const handleSave = async () => {
    if (!accessToken) return;
    if (!form.name.trim()) {
      setError("氏名を入力してください");
      // フォーカスを氏名フィールドへ戻し、スクリーンリーダーにも通知
      requestAnimationFrame(() => nameInputRef.current?.focus());
      return;
    }
    setStep("running");
    setError(null);

    try {
      let target = sheet;
      if (!target) {
        const detected = await detectTsubameSheet(accessToken);
        if (!detected) {
          throw new Error(
            "つばめカードのスプレッドシートが見つかりません。先に iOS 版でシートを作成してください。",
          );
        }
        target = detected;
        setSheet(detected);
      }
      await ensureTagsColumn(target.id, accessToken).catch(() => {});

      const card = buildCard();
      await appendCards(target.id, [card], accessToken);
      setStatus("idle"); // 一覧の再フェッチを促す
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
      setStep("error");
    }
  };

  const reset = () => {
    setForm(initialForm());
    setTags([]);
    setTagInput("");
    setShowEnglish(false);
    setError(null);
    setStep("form");
  };

  // ============================================================
  // Render
  // ============================================================

  if (step === "running") {
    return (
      <div className="px-6 py-16 text-center text-sm text-navySoft">
        Google Sheets に書き込んでいます...
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="rounded-lg border border-washiDeep bg-white px-6 py-12 text-center">
        <p className="text-lg font-medium text-navy">
          ✅ 1 件を追加しました
        </p>
        <p className="mt-2 text-xs text-inkLight">シートの末尾に追加されました。</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button type="button" onClick={reset} className="btn-secondary">
            続けて入力
          </button>
          {sheet && (
            <a
              href={`https://docs.google.com/spreadsheets/d/${sheet.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Google Sheets で確認 ↗
            </a>
          )}
          <button
            type="button"
            onClick={() => navigate("/cards")}
            className="btn-primary"
          >
            一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-6 py-12 text-center">
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={() => setStep("form")}
            className="btn-secondary"
          >
            戻る
          </button>
          <button type="button" onClick={handleSave} className="btn-primary">
            再試行
          </button>
        </div>
      </div>
    );
  }

  // step === "form"
  const nameInvalid = !form.name.trim() && error === "氏名を入力してください";
  const nameErrorId = "manual-name-error";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSave();
      }}
      noValidate
      className="space-y-6"
    >
      <Section title="基本情報">
        <Field label="氏名" required>
          <input
            ref={nameInputRef}
            type="text"
            value={form.name}
            onChange={set("name")}
            placeholder="例: 山田 太郎"
            aria-required="true"
            aria-invalid={nameInvalid || undefined}
            aria-describedby={nameInvalid ? nameErrorId : undefined}
            className={`form-input ${nameInvalid ? "border-red-400 focus:border-red-500 focus:ring-red-300" : ""}`}
          />
          {nameInvalid && (
            <p
              id={nameErrorId}
              role="alert"
              className="mt-1 text-xs text-red-600"
            >
              氏名を入力してください
            </p>
          )}
        </Field>
        <Field label="取得日">
          <input
            type="date"
            value={form.acquiredAt}
            onChange={set("acquiredAt")}
            className="form-input"
          />
        </Field>
        <Field label="会社">
          <input
            type="text"
            value={form.company}
            onChange={set("company")}
            placeholder="例: 株式会社ABC"
            className="form-input"
          />
        </Field>
        <Field label="部署">
          <input
            type="text"
            value={form.department}
            onChange={set("department")}
            placeholder="例: 営業1課"
            className="form-input"
          />
        </Field>
        <Field label="役職">
          <input
            type="text"
            value={form.title}
            onChange={set("title")}
            placeholder="例: 部長"
            className="form-input"
          />
        </Field>
      </Section>

      <Section title="連絡先">
        <Field label="電話">
          <input
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="例: 03-1234-5678"
            className="form-input"
          />
        </Field>
        <Field label="携帯">
          <input
            type="tel"
            value={form.mobile}
            onChange={set("mobile")}
            placeholder="例: 090-1234-5678"
            className="form-input"
          />
        </Field>
        <Field label="FAX">
          <input
            type="tel"
            value={form.fax}
            onChange={set("fax")}
            className="form-input"
          />
        </Field>
        <Field label="メール 1">
          <input
            type="email"
            value={form.email1}
            onChange={set("email1")}
            placeholder="例: yamada@abc.example"
            className="form-input"
          />
        </Field>
        <Field label="メール 2">
          <input
            type="email"
            value={form.email2}
            onChange={set("email2")}
            className="form-input"
          />
        </Field>
        <Field label="URL">
          <input
            type="url"
            value={form.url}
            onChange={set("url")}
            placeholder="例: https://abc.example/"
            className="form-input"
          />
        </Field>
      </Section>

      <Section title="住所">
        <Field label="郵便番号">
          <input
            type="text"
            value={form.postalCode}
            onChange={set("postalCode")}
            placeholder="例: 100-0001"
            className="form-input"
          />
        </Field>
        <Field label="住所" wide>
          <input
            type="text"
            value={form.address}
            onChange={set("address")}
            placeholder="例: 東京都千代田区丸の内1-1-1"
            className="form-input"
          />
        </Field>
      </Section>

      <Section title="メモ">
        <Field label="メモ" wide>
          <textarea
            value={form.memo}
            onChange={set("memo")}
            rows={3}
            placeholder="例: 紹介者・話した内容・次回アクション"
            className="form-input min-h-[5rem] resize-y"
          />
        </Field>
      </Section>

      <Section title="タグ">
        <div className="md:col-span-2">
          <div
            className="flex min-h-[2.5rem] flex-wrap gap-2 rounded-md border border-washiDeep bg-washi p-2"
            aria-label="付与中のタグ"
          >
            {tags.length === 0 && (
              <span className="text-xs text-inkLight">
                タグはまだありません
              </span>
            )}
            {tags.map((t) => (
              <TagBadge
                key={t}
                tag={t}
                size="md"
                onRemove={() => setTags((prev) => removeTag(prev, t))}
              />
            ))}
          </div>
          <label htmlFor="manual-tag-input" className="sr-only">
            タグを追加
          </label>
          <div className="mt-2 flex gap-2">
            <input
              id="manual-tag-input"
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitTagInput();
                }
              }}
              placeholder="タグを入力して Enter (例: 重要)"
              className="form-input flex-1"
            />
            <button
              type="button"
              onClick={submitTagInput}
              className="btn-secondary whitespace-nowrap"
            >
              追加
            </button>
          </div>
        </div>
      </Section>

      <div className="rounded-lg border border-washiDeep bg-white p-4">
        <button
          type="button"
          onClick={() => setShowEnglish((v) => !v)}
          className="flex w-full items-center justify-between text-sm font-medium text-navy"
          aria-expanded={showEnglish}
        >
          <span>英語面(任意)</span>
          <span className="text-xs text-inkLight">
            {showEnglish ? "閉じる" : "開く"}
          </span>
        </button>
        {showEnglish && (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="氏名 (英)">
              <input
                type="text"
                value={form.nameEn}
                onChange={set("nameEn")}
                placeholder="Taro Yamada"
                className="form-input"
              />
            </Field>
            <Field label="会社 (英)">
              <input
                type="text"
                value={form.companyEn}
                onChange={set("companyEn")}
                placeholder="ABC Inc."
                className="form-input"
              />
            </Field>
            <Field label="部署 (英)">
              <input
                type="text"
                value={form.departmentEn}
                onChange={set("departmentEn")}
                className="form-input"
              />
            </Field>
            <Field label="役職 (英)">
              <input
                type="text"
                value={form.titleEn}
                onChange={set("titleEn")}
                className="form-input"
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="住所 (英)" wide>
                <input
                  type="text"
                  value={form.addressEn}
                  onChange={set("addressEn")}
                  className="form-input"
                />
              </Field>
            </div>
          </div>
        )}
      </div>

      {error && !nameInvalid && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate("/cards")}
          className="btn-secondary"
        >
          キャンセル
        </button>
        <button type="submit" className="btn-primary">
          保存して追加
        </button>
      </div>
    </form>
  );
}

// ============================================================
// 局所コンポーネント
// ============================================================

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-washiDeep bg-white p-4">
      <h3 className="text-sm font-medium text-navy">{title}</h3>
      <div className="mt-4 grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({
  label,
  required,
  wide,
  children,
}: {
  label: string;
  required?: boolean;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`block text-xs font-medium text-navySoft ${wide ? "md:col-span-2" : ""}`}>
      <span>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
